<template>
  <CContainer fluid class="px-4 py-4">
    <!-- Header with Breadcrumb -->
    <CRow class="mb-3 align-items-center">
      <div class="col d-flex justify-content-between align-items-start">
        <h1 class="h3 mb-1">Controller Configuration</h1>
        <CButton
          color="primary"
          variant="outline"
          :disabled="refreshing"
          @click="refreshValues">
          <CSpinner v-if="refreshing" component="span" size="sm" class="me-1" />
          <CIcon v-else icon="cilReload" class="me-1" />
          Refresh Values
        </CButton>
      </div>
    </CRow>

    <!-- PLC Info Card -->
    <CCard class="mb-4 shadow-sm">
      <CCardBody>
        <div class="row g-4">
          <div class="col-md-3">
            <div class="d-flex align-items-center">
              <CIcon icon="cilMemory" size="xl" class="text-primary me-3" />
              <div>
                <div class="text-muted small">Controller</div>
                <div class="fw-semibold">{{ plc.name }}</div>
                <div class="text-muted small">{{ plc.label }}</div>
              </div>
            </div>
          </div>
          <div class="col-md-2">
            <div class="text-muted small">Version</div>
            <div class="fw-semibold">{{ plc.modbus_firmware_version.name }}</div>
          </div>
          <div class="col-md-2">
            <div class="text-muted small">Slave ID</div>
            <div class="fw-semibold">{{ plc.slave_id }}</div>
          </div>
          <div class="col-md-2">
            <div class="text-muted small">IP Address</div>
            <div class="fw-semibold font-monospace">{{ plc.private_ip || 'N/A' }}</div>
          </div>
          <div class="col-md-3">
            <div class="text-muted small">Last Seen</div>
            <div class="fw-semibold">
              {{ plc.last_seen_at ? formatDate(plc.last_seen_at) : 'Never' }}
            </div>
          </div>
        </div>
      </CCardBody>
    </CCard>

    <CTabs v-model:active-item-key="activeTab" variant="tabs">
      <CTabList variant="tabs">
        <CTab
          v-for="tab in COMMUNICATION_TYPE_TABS"
          :key="tab.key"
          :item-key="tab.key"
          class="tab-item">
          <CIcon :name="tab.icon" class="me-2" />
          {{ tab.label }}
          <CBadge color="primary" shape="rounded-pill" class="ms-1">
            {{ getInterfaceCount(tab.key) }}
          </CBadge>
        </CTab>
        <CTab item-key="configuration" class="tab-item">
          <CIcon icon="cilSettings" />
          Configuration
        </CTab>
      </CTabList>

      <CTabContent>
        <CTabPanel
          v-for="tab in COMMUNICATION_TYPE_TABS"
          :key="tab.key"
          :item-key="tab.key">
          <InterfaceList
            :plc="plc"
            :interfaces="getInterfacesByType(tab.key)"
            :communicationType="tab.key"
            :segments="segments"
            :measurementSubtypes="measurementSubtypes"
            @update-interface="handleUpdateInterface" />
        </CTabPanel>
        <CTabPanel item-key="configuration">
          <ConfigurationPanel :plc="plc" />
        </CTabPanel>
      </CTabContent>
    </CTabs>
  </CContainer>
</template>

<script lang="ts" setup>
  import { computed, ref } from 'vue';
  import { router } from '@inertiajs/vue3';
  import axios from 'axios';
  import InterfaceList from './components/InterfaceList.vue';
  import ConfigurationPanel from './components/ConfigurationPanel.vue';
  import useAuth from '@/composables/useAuth';
  import usePermissions from '@/composables/usePermissions';
  import { useApiCall } from '@/composables/useApi';
  import type { Segment } from '@/types/location';
  import type { CommunicationType, PlcWithInterfaces } from '@/types/plc';
  import type { MeasurementPoint, MeasurementSubtype } from '@/types/measurementPoint';
  import { COMMUNICATION_TYPE_TABS } from './types';

  const { currentSite, pageProps, routePath } = useAuth<{
    plc: PlcWithInterfaces;
    measurementSubtypes: MeasurementSubtype[];
  }>();
  const segments = computed(() => currentSite.value?.segments || []);

  const plc = computed(() => pageProps.value.plc);
  const measurementSubtypes = computed(() => pageProps.value.measurementSubtypes);

  const { execute } = useApiCall();

  const activeTab = ref<CommunicationType>('digital_input');
  const refreshing = ref(false);

  function getInterfacesByType(type: CommunicationType) {
    return plc.value.interfaces.filter((i) => i.communication_type === type);
  }

  function getInterfaceCount(type: CommunicationType) {
    return getInterfacesByType(type).length;
  }

  function formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleString();
  }

  async function refreshValues() {
    refreshing.value = true;

    const url = routePath('plcs_refresh_interfaces', { id: plc.value.id });
    const { success } = await execute(
      () => axios.post(url),
      {
        showSuccessToast: true,
        successMessage: 'Refresh started — values will update shortly',
        showErrorToast: true,
        errorTitle: 'Could not start refresh'
      }
    );

    if (success) {
      // Give the job a moment to complete, then reload page data so UI shows fresh values.
      setTimeout(() => {
        router.reload({ only: ['plc'] });
        refreshing.value = false;
      }, 3000);
    } else {
      refreshing.value = false;
    }
  }

  async function handleUpdateInterface(
    updatedMeasurementPoint: MeasurementPoint,
    siblingMeasurementPoints: MeasurementPoint[]
  ) {
    const pointsMap = new Map<MeasurementPoint['id'], MeasurementPoint>(
      [updatedMeasurementPoint, ...siblingMeasurementPoints].map((p) => [p.id, p])
    );

    let remaining = pointsMap.size;
    for (const iface of plc.value.interfaces) {
      for (const mapping of iface.register_mappings) {
        const updatedPoint = pointsMap.get(mapping.measurement_point.id);
        if (updatedPoint) {
          mapping.measurement_point = updatedPoint;
          if (--remaining === 0)
            return;
        }
      }
    }
  }
</script>

<style scoped>
  .nav-link {
    cursor: pointer;
  }
</style>
